import { useState, useEffect } from 'react';
import { serviceManagementRequest } from '../services/serviceManagementApi';

export interface CSVRow {
  [key: string]: string;
}

export interface ServiceTask {
  id: string;
  initiative_id: string;
  nombre_iniciativa?: string | null;
  titulo: string;
  descripcion?: string | null;
  asignado_a?: string | null;
  asignado_email?: string | null;
  semana_inicio?: string | null;
  semana_fin?: string | null;
  estado: 'pending' | 'in_progress' | 'completed';
  created_at?: string | null;
  updated_at?: string | null;
  legacy_initiative_id?: string | null;
}

export interface ServiceStatusDescription {
  id: string;
  initiative_id: string;
  description: string;
  created_at: string;
}

export interface ServiceInitiativeArea {
  codigo: string;
  descripcion: string;
  created_at?: string | null;
}

export interface DirectoryUser {
  id?: string | number;
  mail?: string | null;
  nombre?: string | null;
  apellidos?: string | null;
  nombre_completo?: string | null;
  department?: string | null;
  center?: string | null;
  title?: string | null;
  sede?: string | null;
  phone?: string | null;
  extension?: string | null;
  search_text?: string | null;
}

interface ServiceInitiative {
  id: string;
  csv_sync_id?: string | null;
  nombre_del_servicio?: string | null;
  responsables?: string | null;
  responsables_email?: string | null;
  tecnicos?: string | null;
  tecnicos_email?: string | null;
  ruta_carpeta?: string | null;
  funcionalidad?: string | null;
  estado_implementacion?: string | null;
  centro?: string | null;
  descripcion?: string | null;
  modificado_por?: string | null;
  modificado?: string | null;
  revisor?: string | null;
  revisor_email?: string | null;
  roles?: string[] | null;
  teams_url?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  is_favorite?: boolean | null;
  oculta?: boolean | null;
  codigo_area?: string | null;
}

export const SERVICE_MANAGEMENT_COLUMNS = [
  'NombreProceso',
  'Responsable servicio',
  'Técnico',
  'Ruta carpeta',
  'Funcionalidad',
  'Estado_implementacion',
  'Centro',
  'Role',
  'descripcion',
  'Modificado por',
  'Modificado',
  'Revisor/a',
  'ID',
  'Whatsapp',
  'Usuario',
  'Fecha',
  'Sinidentificacion',
  'Fecha_novedad_proceso',
];

function getServiceName(row: CSVRow): string {
  return (row['NombreProceso'] || row['Nombre del servicio'] || '').trim();
}

export function normalizeFuncionalidadValue(value?: string | null): string {
  const trimmed = String(value || '').trim();
  const normalized = trimmed.toLowerCase().replace(/[\s_-]+/g, '');
  return normalized === 'sharepoint' ? 'GPT-CEU' : trimmed;
}

function splitList(value?: string | null): string[] {
  return (value || '')
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);
}

function toUserJson(namesValue?: string | null, emailsValue?: string | null): string {
  const names = splitList(namesValue);
  const emails = splitList(emailsValue);
  const length = Math.max(names.length, emails.length);

  if (!length) return '';

  return JSON.stringify(
    Array.from({ length }, (_, index) => ({
      nombre: names[index] || '',
      user_mail: emails[index] || '',
    })).filter((item) => item.nombre || item.user_mail)
  );
}

function parseUsersField(raw?: string): { names: string; emails: string } {
  if (!raw || !raw.trim()) return { names: '', emails: '' };

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const names = parsed
        .map((item) => String(item?.nombre || '').trim())
        .filter(Boolean);
      const emails = parsed
        .map((item) => String(item?.user_mail || item?.email || item?.mail || '').trim())
        .filter(Boolean);
      return { names: names.join(';'), emails: emails.join(';') };
    }
  } catch {
    // fallback below
  }

  if (raw.includes('@')) return { names: '', emails: raw };
  return { names: raw, emails: '' };
}

function parseRolesField(raw?: string): string[] {
  if (!raw || !raw.trim()) return [];

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter(Boolean);
    }
  } catch {
    // fallback below
  }

  return raw
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseRowDate(value?: string): string | null {
  if (!value || !value.trim()) return null;
  const raw = value.trim();
  const ddmmyyyy = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (ddmmyyyy) {
    const [, d, m, y, hh = '0', mm = '0', ss = '0'] = ddmmyyyy;
    const dt = new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss));
    return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
  }

  const dt = new Date(raw);
  return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
}

function formatDatabaseDate(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('es-ES');
}

function mapServiceInitiativeToCSVRow(initiative: ServiceInitiative, fallback?: CSVRow): CSVRow {
  const responsables = toUserJson(initiative.responsables, initiative.responsables_email);
  const tecnicos = toUserJson(initiative.tecnicos, initiative.tecnicos_email);
  const revisor = toUserJson(initiative.revisor, initiative.revisor_email);
 
  
  return {
    ...(fallback || {}),
    __database_id: initiative.id,
    __is_favorite: String(Boolean(initiative.is_favorite)),
    __is_hidden: String(Boolean(initiative.oculta)),
    __codigo_area: (initiative.codigo_area || '').toUpperCase().trim(),
    NombreProceso: initiative.nombre_del_servicio || fallback?.NombreProceso || fallback?.['Nombre del servicio'] || '',
    'Nombre del servicio': initiative.nombre_del_servicio || fallback?.['Nombre del servicio'] || fallback?.NombreProceso || '',
    'Responsable servicio': responsables || fallback?.['Responsable servicio'] || '',
    'Técnico': tecnicos || fallback?.['Técnico'] || '',
    'Ruta carpeta': initiative.ruta_carpeta || fallback?.['Ruta carpeta'] || '',
    Funcionalidad: normalizeFuncionalidadValue(initiative.funcionalidad || fallback?.Funcionalidad || ''),
    Estado_implementacion: initiative.estado_implementacion || fallback?.Estado_implementacion || fallback?.['Estado implementación'] || '',
    'Estado implementación': initiative.estado_implementacion || fallback?.['Estado implementación'] || fallback?.Estado_implementacion || '',
    Centro: initiative.centro || fallback?.Centro || '',
    Role: JSON.stringify(initiative.roles || []),
    ROL: JSON.stringify(initiative.roles || []),
    descripcion: initiative.descripcion || fallback?.descripcion || fallback?.['Descripción'] || '',
    'Descripción': initiative.descripcion || fallback?.['Descripción'] || fallback?.descripcion || '',
    'Modificado por': initiative.modificado_por || fallback?.['Modificado por'] || '',
    Modificado: formatDatabaseDate(initiative.modificado || initiative.updated_at) || fallback?.Modificado || '',
    'Revisor/a': revisor || fallback?.['Revisor/a'] || fallback?.['Revisor/a '] || '',
    'Revisor/a ': revisor || fallback?.['Revisor/a '] || fallback?.['Revisor/a'] || '',
    ID: initiative.csv_sync_id || fallback?.ID || '',
  };
}

export async function fetchServiceInitiatives(): Promise<ServiceInitiative[]> {
  return serviceManagementRequest<ServiceInitiative[]>('/api/service-management/initiatives');
}

export async function fetchServiceInitiativeAreas(): Promise<ServiceInitiativeArea[]> {
  return serviceManagementRequest<ServiceInitiativeArea[]>('/api/service-management/areas');
}

export async function fetchDirectoryUsers(): Promise<DirectoryUser[]> {
  return serviceManagementRequest<DirectoryUser[]>('/api/service-management/users');
}

export async function fetchServiceTasks(initiativeId: string): Promise<ServiceTask[]> {
  if (!initiativeId) return [];
  const query = new URLSearchParams({ initiativeId });
  return serviceManagementRequest<ServiceTask[]>(`/api/service-management/tasks?${query}`);
}

export async function fetchAllServiceTasks(): Promise<ServiceTask[]> {
  return serviceManagementRequest<ServiceTask[]>('/api/service-management/tasks');
}

export async function updateServiceTask(taskId: string, updates: Partial<ServiceTask>): Promise<ServiceTask> {
  return serviceManagementRequest<ServiceTask>(
    `/api/service-management/tasks/${encodeURIComponent(taskId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }
  );
}

export async function createServiceTask(task: Omit<ServiceTask, 'id' | 'created_at' | 'updated_at'>): Promise<ServiceTask> {
  return serviceManagementRequest<ServiceTask>('/api/service-management/tasks', {
    method: 'POST',
    body: JSON.stringify(task),
  });
}

export async function setServiceInitiativeFavorite(row: CSVRow, value: boolean): Promise<CSVRow> {
  const id = row.__database_id;
  if (!id) throw new Error('No se puede actualizar favorito sin ID de iniciativa.');
  const saved = await serviceManagementRequest<ServiceInitiative>(
    `/api/service-management/initiatives/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ is_favorite: value }),
    }
  );
  return mapServiceInitiativeToCSVRow(saved, row);
}

export async function fetchLatestServiceStatusDescriptions(): Promise<ServiceStatusDescription[]> {
  return serviceManagementRequest<ServiceStatusDescription[]>(
    '/api/service-management/status-descriptions?latest=true'
  );
}

export async function fetchServiceStatusDescriptions(initiativeId: string): Promise<ServiceStatusDescription[]> {
  if (!initiativeId) return [];
  const query = new URLSearchParams({ initiativeId });
  return serviceManagementRequest<ServiceStatusDescription[]>(
    `/api/service-management/status-descriptions?${query}`
  );
}

export async function createServiceStatusDescription(
  initiativeId: string,
  description: string
): Promise<ServiceStatusDescription> {
  return serviceManagementRequest<ServiceStatusDescription>(
    '/api/service-management/status-descriptions',
    {
      method: 'POST',
      body: JSON.stringify({ initiative_id: initiativeId, description }),
    }
  );
}

function rowToInitiativePayload(row: CSVRow): Partial<ServiceInitiative> {
  const responsables = parseUsersField(row['Responsable servicio']);
  const tecnicos = parseUsersField(row['Técnico']);
  const revisor = parseUsersField(row['Revisor/a'] || row['Revisor/a ']);

  return {
    nombre_del_servicio: getServiceName(row),
    responsables: responsables.names || null,
    responsables_email: responsables.emails || null,
    tecnicos: tecnicos.names || null,
    tecnicos_email: tecnicos.emails || null,
    ruta_carpeta: row['Ruta carpeta'] || null,
    funcionalidad: normalizeFuncionalidadValue(row['Funcionalidad']),
    estado_implementacion: row['Estado_implementacion'] || row['Estado implementación'] || '',
    centro: row['Centro'] || null,
    descripcion: row['descripcion'] || row['Descripción'] || '',
    modificado_por: row['Modificado por'] || null,
    modificado: parseRowDate(row['Modificado']),
    revisor: revisor.names || null,
    revisor_email: revisor.emails || null,
    roles: parseRolesField(row['Role'] || row['ROL']),
    csv_sync_id: row['ID'] || null,
    codigo_area: row.__codigo_area || null,
  };
}

export async function upsertServiceInitiativeRow(row: CSVRow): Promise<CSVRow> {
  const payload = rowToInitiativePayload(row);
  const existingId = row.__database_id;

  if (!payload.nombre_del_servicio) {
    throw new Error('El campo NombreProceso es obligatorio.');
  }

  const endpoint = existingId
    ? `/api/service-management/initiatives/${encodeURIComponent(existingId)}`
    : '/api/service-management/initiatives';
  const saved = await serviceManagementRequest<ServiceInitiative>(endpoint, {
    method: existingId ? 'PATCH' : 'POST',
    body: JSON.stringify(payload),
  });
  return mapServiceInitiativeToCSVRow(saved, row);
}

export async function deleteServiceInitiativeRow(row: CSVRow): Promise<void> {
  const id = row.__database_id;
  if (!id) throw new Error('No se puede eliminar una iniciativa sin ID.');
  await serviceManagementRequest<void>(
    `/api/service-management/initiatives/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
    }
  );
}

export function useCSVData() {
  const [data, setData] = useState<CSVRow[]>([]);
  const [columns, setColumns] = useState<string[]>(SERVICE_MANAGEMENT_COLUMNS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const initiativeRows = await fetchServiceInitiatives();
        setData(initiativeRows.map((row) => mapServiceInitiativeToCSVRow(row)));
        setColumns(SERVICE_MANAGEMENT_COLUMNS);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error desconocido al cargar los datos');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return { data, columns, loading, error };
}
